/**
 * Saved looks in localStorage.
 *
 * Two keys: LOOKS_KEY holds the named collection as one JSON array, LAST_KEY
 * the in-progress look so a reload brings the cat being edited straight back.
 * The collection is one array under one key rather than a key per record
 * because every operation here wants the whole set anyway (list newest-first,
 * de-duplicate a name), and a single getItem/setItem pair means a record can
 * never be half-written.
 *
 * Storage is treated as hostile.  It can be absent, throw on mere property
 * access (sandboxed iframes), throw on setItem (Safari private mode, quota),
 * or hold hand-edited nonsense.  Every export degrades instead of throwing --
 * listLooks() to [], saveLook() to null -- because the game must play
 * identically with storage gone.  And every look that leaves this module has
 * passed through normalize(), so nothing downstream defends against a
 * truncated or out-of-range record.
 */
import { normalize } from "@catworld/shared";
import { getAdopting } from "../legacy-storage.js";

const LOOKS_KEY = "catworld.looks";
const LAST_KEY = "catworld.looks.last";

/** What the two keys were called before the game was renamed.  Read, never
    written: the first read of each adopts it (legacy-storage.js). */
const LEGACY_LOOKS_KEY = "lilysworld.looks";
const LEGACY_LAST_KEY = "lilysworld.looks.last";
const FALLBACK_NAME = "Cat";
const NAME_MAX = 40;          // matches normalize()'s cap on look.name

/** localStorage, or null where even touching it throws. */
function storage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

/** Parse the collection, drop anything not record-shaped, newest first. */
function readAll() {
  const s = storage();
  if (!s) return [];
  let raw;
  try {
    raw = JSON.parse(getAdopting(s, LOOKS_KEY, LEGACY_LOOKS_KEY));
  } catch {
    return [];
  }
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const r of raw) {
    if (!r || typeof r !== "object" || typeof r.id !== "string") continue;
    out.push({
      id: r.id,
      name: typeof r.name === "string" && r.name ? r.name.slice(0, NAME_MAX + 8) : FALLBACK_NAME,
      look: normalize(r.look),
      savedAt: Number.isFinite(r.savedAt) ? r.savedAt : 0,
    });
  }
  // Stable sort, and new records are prepended on save, so two saves inside
  // one millisecond still list in save order.
  out.sort((a, b) => b.savedAt - a.savedAt);
  return out;
}

function writeAll(records) {
  const s = storage();
  if (!s) return false;
  try {
    s.setItem(LOOKS_KEY, JSON.stringify(records));
    return true;
  } catch {
    return false;             // full or read-only; the caller reports, nothing crashes
  }
}

function makeId() {
  // crypto.randomUUID needs a secure context, and file:// or plain-http LAN
  // testing is not one.  The fallback only has to be unique within one
  // player's saved-cats list.
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** "Whiskers" -> "Whiskers 2" -> "Whiskers 3"...  Overwriting someone's cat
    because they reused a name would be data loss with no undo. */
function dedupe(name, taken) {
  if (!taken.has(name)) return name;
  for (let n = 2; ; n++) {
    const candidate = `${name} ${n}`;
    if (!taken.has(candidate)) return candidate;
  }
}

function cleanName(name) {
  return String(name ?? "").trim().slice(0, NAME_MAX) || FALLBACK_NAME;
}

/** Every saved record { id, name, look, savedAt }, newest first.
    [] on any storage failure. */
export function listLooks() {
  return readAll();
}

/**
 * Save `look` under `name` (de-duplicated, so read back record.name).
 * Returns the new record, or null when storage refused the write.
 */
export function saveLook(name, look) {
  const records = readAll();
  const finalName = dedupe(cleanName(name), new Set(records.map((r) => r.name)));
  const normalized = normalize(look);
  normalized.name = finalName;
  const record = { id: makeId(), name: finalName, look: normalized, savedAt: Date.now() };
  return writeAll([record, ...records]) ? record : null;
}

/** True when the record existed and the shrunk collection was written. */
export function deleteLook(id) {
  const records = readAll();
  const kept = records.filter((r) => r.id !== id);
  if (kept.length === records.length) return false;
  return writeAll(kept);
}

/** Rename a record (same de-duplication as saveLook, against the others).
    Returns the updated record, or null when it is missing or the write failed. */
export function renameLook(id, name) {
  const records = readAll();
  const record = records.find((r) => r.id === id);
  if (!record) return null;
  const others = new Set(records.filter((r) => r !== record).map((r) => r.name));
  record.name = dedupe(cleanName(name), others);
  record.look.name = record.name;
  return writeAll(records) ? record : null;
}

/** Persist the in-progress look.  Best effort: losing it costs a reload's
    worth of slider drags, not a saved cat, so failure is silent. */
export function rememberLast(look) {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(LAST_KEY, JSON.stringify(normalize(look)));
  } catch {}
}

/** The in-progress look from the previous session, normalized, or null. */
export function loadLast() {
  const s = storage();
  if (!s) return null;
  try {
    const raw = JSON.parse(getAdopting(s, LAST_KEY, LEGACY_LAST_KEY));
    return raw && typeof raw === "object" ? normalize(raw) : null;
  } catch {
    return null;
  }
}
